/**
 * WebsiteManager Component
 * Manages website images (focal points) and texts from the Owner Dashboard
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ownerAPI } from '../services/api';
import './WebsiteManager.css';

// Website base URL for image previews
const WEBSITE_BASE = process.env.REACT_APP_WEBSITE_URL || 'https://www.derstarkefranz.at';

// Page labels for display
const PAGE_LABELS = {
  'index': 'Startseite',
  'training': 'Training (Übersicht)',
  'training/personal-training': 'Personal Training',
  'training/gesundheitscheck': 'Gesundheits-Check',
  'training/mobilitaet': 'Mobilität',
  'training/stabilitaet': 'Stabilität',
  'training/kraft-40plus': 'Kraft 40+',
  'training/schmerzfrei': 'Schmerzfrei',
  'training/reha': 'Reha-Begleitung',
  'training/box-training': 'Box-Training',
  'team': 'Team',
  'standorte': 'Standorte',
  'preise': 'Preise',
  'kontakt': 'Kontakt'
};

const SECTION_LABELS = {
  'hero': 'Hero',
  'gallery': 'Galerie',
  'problem': 'Kennst du das?',
  'solution': 'Willkommen im Franz',
  'features': 'USP Features',
  'training': 'Trainingskompetenzen',
  'team': 'Team',
  'process': 'Prozess',
  'testimonials': 'Kundenstimmen',
  'pricing': 'Preise',
  'scarcity': 'Scarcity-Band',
  'locations': 'Standorte',
  'contact': 'Kontakt',
  'cta': 'Call to Action',
  'content': 'Inhalt',
  'footer': 'Footer'
};

const WebsiteManager = ({ showMessage }) => {
  const [subTab, setSubTab] = useState('images');
  const [images, setImages] = useState([]);
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageFilter, setPageFilter] = useState('');
  const [editingImage, setEditingImage] = useState(null);
  const [editingTexts, setEditingTexts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = pageFilter ? { page: pageFilter } : {};
      if (subTab === 'images') {
        const res = await ownerAPI.getWebsiteImages(params);
        setImages(res.data);
      } else {
        const res = await ownerAPI.getWebsiteTexts(params);
        setTexts(res.data);
        // Initialize editing state
        const edits = {};
        res.data.forEach(t => { edits[t.id] = t.current_text; });
        setEditingTexts(edits);
      }
    } catch (err) {
      showMessage('Fehler beim Laden der Daten', 'error');
    } finally {
      setLoading(false);
    }
  }, [subTab, pageFilter, showMessage]);

  useEffect(() => { loadData(); }, [loadData]);

  // Get unique pages for filter dropdown
  const pages = [...new Set([...images, ...texts].map(i => i.page))].sort();

  // =====================
  // IMAGE HANDLERS
  // =====================

  const handleFocalPointSave = async (imageId, objectPosition) => {
    setSavingId(imageId);
    try {
      await ownerAPI.updateWebsiteImage(imageId, { object_position: objectPosition });
      setImages(prev => prev.map(img =>
        img.id === imageId ? { ...img, object_position: objectPosition } : img
      ));
      setEditingImage(null);
      showMessage('Bildausschnitt gespeichert');
    } catch (err) {
      showMessage('Fehler beim Speichern', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleImageUpload = async (imageId, file) => {
    setSavingId(imageId);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await ownerAPI.uploadWebsiteImage(imageId, formData);
      setImages(prev => prev.map(img =>
        img.id === imageId ? res.data : img
      ));
      showMessage('Bild hochgeladen');
    } catch (err) {
      showMessage('Fehler beim Hochladen', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleImageRemove = async (imageId) => {
    setSavingId(imageId);
    try {
      const res = await ownerAPI.removeWebsiteImage(imageId);
      setImages(prev => prev.map(img =>
        img.id === imageId ? res.data : img
      ));
      showMessage('Ersatzbild entfernt');
    } catch (err) {
      showMessage('Fehler beim Entfernen', 'error');
    } finally {
      setSavingId(null);
    }
  };

  // =====================
  // TEXT HANDLERS
  // =====================

  const handleTextSave = async (textId) => {
    setSavingId(textId);
    try {
      const res = await ownerAPI.updateWebsiteText(textId, { current_text: editingTexts[textId] });
      setTexts(prev => prev.map(t => t.id === textId ? res.data : t));
      showMessage('Text gespeichert');
    } catch (err) {
      showMessage('Fehler beim Speichern', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleTextReset = async (textId) => {
    setSavingId(textId);
    try {
      const res = await ownerAPI.resetWebsiteText(textId);
      setTexts(prev => prev.map(t => t.id === textId ? res.data : t));
      setEditingTexts(prev => ({ ...prev, [textId]: res.data.current_text }));
      showMessage('Text zurückgesetzt');
    } catch (err) {
      showMessage('Fehler beim Zurücksetzen', 'error');
    } finally {
      setSavingId(null);
    }
  };

  // =====================
  // GROUP BY PAGE
  // =====================

  const groupByPage = (items) => {
    const grouped = {};
    items.forEach(item => {
      if (!grouped[item.page]) grouped[item.page] = [];
      grouped[item.page].push(item);
    });
    return grouped;
  };

  // =====================
  // RENDER: IMAGES TAB
  // =====================

  const renderImages = () => {
    const grouped = groupByPage(images);

    if (images.length === 0 && !loading) {
      return <p className="wm-empty">Keine Bilder gefunden. Bitte Seed-Script ausführen.</p>;
    }

    return (
      <div className="wm-images">
        {Object.entries(grouped).map(([page, pageImages]) => (
          <div key={page} className="wm-page-group">
            <h3 className="wm-page-title">{PAGE_LABELS[page] || page}</h3>
            <div className="wm-image-grid">
              {pageImages.map(img => (
                <div key={img.id} className="wm-image-card">
                  <div
                    className="wm-image-thumb"
                    style={{
                      backgroundImage: `url(${img.replacement_url || `${WEBSITE_BASE}/${img.original_src}`})`,
                      backgroundPosition: img.object_position || '50% 50%'
                    }}
                    onClick={() => setEditingImage(img)}
                  />
                  <div className="wm-image-info">
                    <span className="wm-image-label">
                      {SECTION_LABELS[img.section] || img.section}
                    </span>
                    <span className="wm-image-key">{img.image_key}</span>
                    <span className="wm-image-pos">{img.object_position || '50% 50%'}</span>
                  </div>
                  <div className="wm-image-actions">
                    <button
                      className="wm-btn wm-btn-sm"
                      onClick={() => setEditingImage(img)}
                      disabled={savingId === img.id}
                    >
                      Ausschnitt
                    </button>
                    <label className="wm-btn wm-btn-sm wm-btn-outline">
                      Ersetzen
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => {
                          if (e.target.files[0]) handleImageUpload(img.id, e.target.files[0]);
                        }}
                      />
                    </label>
                    {img.replacement_url && (
                      <button
                        className="wm-btn wm-btn-sm wm-btn-danger"
                        onClick={() => handleImageRemove(img.id)}
                        disabled={savingId === img.id}
                      >
                        Original
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // =====================
  // RENDER: TEXTS TAB
  // =====================

  const renderTexts = () => {
    const grouped = groupByPage(texts);

    if (texts.length === 0 && !loading) {
      return <p className="wm-empty">Keine Texte gefunden. Bitte Seed-Script ausführen.</p>;
    }

    return (
      <div className="wm-texts">
        {Object.entries(grouped).map(([page, pageTexts]) => (
          <div key={page} className="wm-page-group">
            <h3 className="wm-page-title">{PAGE_LABELS[page] || page}</h3>
            {pageTexts.map(txt => {
              const isModified = txt.current_text !== txt.original_text;
              const isLong = txt.current_text.length > 80;

              return (
                <div key={txt.id} className={`wm-text-item ${isModified ? 'wm-text-modified' : ''}`}>
                  <div className="wm-text-header">
                    <span className="wm-text-label">
                      {SECTION_LABELS[txt.section] || txt.section} · {txt.element_type}
                    </span>
                    <span className="wm-text-key">{txt.text_key}</span>
                  </div>
                  {isLong ? (
                    <textarea
                      className="wm-text-input"
                      value={editingTexts[txt.id] || ''}
                      onChange={(e) => setEditingTexts(prev => ({ ...prev, [txt.id]: e.target.value }))}
                      rows={3}
                    />
                  ) : (
                    <input
                      type="text"
                      className="wm-text-input"
                      value={editingTexts[txt.id] || ''}
                      onChange={(e) => setEditingTexts(prev => ({ ...prev, [txt.id]: e.target.value }))}
                    />
                  )}
                  <div className="wm-text-actions">
                    <button
                      className="wm-btn wm-btn-sm"
                      onClick={() => handleTextSave(txt.id)}
                      disabled={savingId === txt.id || editingTexts[txt.id] === txt.current_text}
                    >
                      {savingId === txt.id ? 'Speichern...' : 'Speichern'}
                    </button>
                    {isModified && (
                      <button
                        className="wm-btn wm-btn-sm wm-btn-outline"
                        onClick={() => handleTextReset(txt.id)}
                        disabled={savingId === txt.id}
                      >
                        Zurücksetzen
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // =====================
  // MAIN RENDER
  // =====================

  return (
    <div className="wm-container">
      <h2>Website verwalten</h2>
      <p style={{ color: '#6b7d8e', marginBottom: 24 }}>
        Bilder-Ausschnitte anpassen und Texte der Marketing-Website bearbeiten.
      </p>

      <div className="wm-toolbar">
        <div className="wm-tabs">
          <button
            className={`wm-tab ${subTab === 'images' ? 'active' : ''}`}
            onClick={() => setSubTab('images')}
          >
            Bilder ({images.length})
          </button>
          <button
            className={`wm-tab ${subTab === 'texts' ? 'active' : ''}`}
            onClick={() => setSubTab('texts')}
          >
            Texte ({texts.length})
          </button>
        </div>
        <select
          className="wm-filter"
          value={pageFilter}
          onChange={(e) => setPageFilter(e.target.value)}
        >
          <option value="">Alle Seiten</option>
          {pages.map(p => (
            <option key={p} value={p}>{PAGE_LABELS[p] || p}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="wm-loading">Laden...</div>
      ) : (
        <>
          {subTab === 'images' && renderImages()}
          {subTab === 'texts' && renderTexts()}
        </>
      )}

      {/* Focal Point Modal */}
      {editingImage && (
        <FocalPointPicker
          image={editingImage}
          websiteBase={WEBSITE_BASE}
          onSave={handleFocalPointSave}
          onClose={() => setEditingImage(null)}
          saving={savingId === editingImage.id}
        />
      )}
    </div>
  );
};

// =====================
// FOCAL POINT PICKER
// =====================

const FocalPointPicker = ({ image, websiteBase, onSave, onClose, saving }) => {
  const parsePosition = (pos) => {
    const parts = (pos || '50% 50%').match(/(\d+)%\s+(\d+)%/);
    return parts ? { x: parseInt(parts[1]), y: parseInt(parts[2]) } : { x: 50, y: 50 };
  };

  const [position, setPosition] = useState(parsePosition(image.object_position));
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const imgSrc = image.replacement_url || `${websiteBase}/${image.original_src}`;

  const handleClick = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    setPosition({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    });
  };

  const handleSave = () => {
    onSave(image.id, `${position.x}% ${position.y}%`);
  };

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="fp-overlay" onClick={onClose}>
      <div className="fp-modal" onClick={(e) => e.stopPropagation()} ref={containerRef}>
        <div className="fp-header">
          <h3>Bildausschnitt anpassen</h3>
          <span className="fp-image-name">{image.image_key}</span>
          <button className="fp-close" onClick={onClose}>&times;</button>
        </div>

        <div className="fp-body">
          <div className="fp-editor">
            <p className="fp-instruction">Klicke auf den gewünschten Fokuspunkt im Bild:</p>
            <div className="fp-image-container" onClick={handleClick}>
              <img ref={imgRef} src={imgSrc} alt={image.alt_text || 'Bild'} draggable={false} />
              <div
                className="fp-crosshair"
                style={{ left: `${position.x}%`, top: `${position.y}%` }}
              />
            </div>
          </div>

          <div className="fp-preview-section">
            <p className="fp-instruction">Vorschau (wie auf der Website):</p>
            <div className="fp-previews">
              <div className="fp-preview-item">
                <span>Breit (16:9)</span>
                <div
                  className="fp-preview-box fp-preview-wide"
                  style={{
                    backgroundImage: `url(${imgSrc})`,
                    backgroundPosition: `${position.x}% ${position.y}%`
                  }}
                />
              </div>
              <div className="fp-preview-item">
                <span>Quadrat (1:1)</span>
                <div
                  className="fp-preview-box fp-preview-square"
                  style={{
                    backgroundImage: `url(${imgSrc})`,
                    backgroundPosition: `${position.x}% ${position.y}%`
                  }}
                />
              </div>
              <div className="fp-preview-item">
                <span>Hochformat (3:4)</span>
                <div
                  className="fp-preview-box fp-preview-tall"
                  style={{
                    backgroundImage: `url(${imgSrc})`,
                    backgroundPosition: `${position.x}% ${position.y}%`
                  }}
                />
              </div>
            </div>
            <p className="fp-position-label">Position: {position.x}% {position.y}%</p>
          </div>
        </div>

        <div className="fp-footer">
          <button className="wm-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
          <button className="wm-btn wm-btn-outline" onClick={onClose}>Abbrechen</button>
          <button
            className="wm-btn wm-btn-outline"
            onClick={() => setPosition({ x: 50, y: 50 })}
          >
            Zentrieren
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebsiteManager;
